import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, Validators, FormBuilder, FormGroup } from '@angular/forms';
import { AcceptInvitationStepBase } from '../../shared/accept-invitation-step-base/accept-invitation-step-base';
import usStates from '../../shared/data/us-states.json';
import { MatDialog } from '@angular/material/dialog';
import { DialogContentComponent } from '../../shared/dialog-content/dialog-content.component';
import { InvitationCommunicationService } from '../../services/invitation-communication.service';
import { OwnerStateService } from '../../services/owner-state.service';
import { dateValidator } from '../../utils/date-validator';
import {
  clearMaskedSsnOnKeydown,
  clearMaskedSsnOnPaste,
  ssnReplacePattern,
  ssnValidator,
} from '../../utils/ssn';
import {
  clearMaskedDobOnKeydown,
  clearMaskedDobOnPaste,
  dobReplacePattern,
  isMaskedDob,
} from '../../utils/date-of-birth';

@Component({
  selector: 'app-personal-details',
  templateUrl: './personal-details.component.html',
  styleUrl: './personal-details.component.scss'
})
export class PersonalDetailsComponent extends AcceptInvitationStepBase<any> implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly commService = inject(InvitationCommunicationService);
  private readonly ownerStateService = inject(OwnerStateService);
  
  public readonly usStates: readonly string[] = usStates;

  public readonly ssnReplacePattern = ssnReplacePattern;
  public readonly dobReplacePattern = dobReplacePattern;
  public ssnMasked: boolean = true;
  public role = this.commService.role;
  private ssnValidators = this.commService.role === 'borrower'
    ? [Validators.required, ssnValidator]
    : [];

  protected override readonly storageKey = 'personalDetailsForm';
  public override get nextUrl(): string {
    return this.commService.role === 'supplier' ? '../about-business' : '../request-credit-line';
  }
  public override readonly previousUrl = null;

  // Calculate the minimum date (18 years ago from today)
  public getMinDate(): Date {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date;
  }

  public override readonly form: FormGroup = inject(FormBuilder).group({
    firstName: ['', Validators.required],
    middleInitial: ['', [Validators.pattern(/^[A-Za-z]$/)]],
    lastName: ['', Validators.required],
    emailAddress: ['', [Validators.required, Validators.email]],
    phoneNumber: ['', Validators.required],
    ssn: ['', this.ssnValidators],
    dateOfBirth: ['', [Validators.required, this.minimumAgeValidator(), dateValidator]],
    streetAddress: ['', Validators.required],
    suiteUnitOffice: [''],
    city: ['', Validators.required],
    state: ['', Validators.required],
    zipCode: ['', Validators.required],
    isOwner: [false]
  });

  private minimumAgeValidator() {
    return (control: any) => {
      if (isMaskedDob(control.value)) {
        return null;
      }

      const date = new Date(control.value);
      const minDate = this.getMinDate();
      
      if (date > minDate) {
        return { tooYoung: true };
      }
      return null;
    };
  }

  protected override afterFormLoaded(): void {
    // Initialize owner state from the (now-loaded) form value
    const isOwner = this.form.get('isOwner')?.value;
    this.ownerStateService.setIsOwner(isOwner);

    // Trigger validation for dateOfBirth field if it has a value
    // This ensures the date format directive and validators work correctly when loading saved data
    setTimeout(() => {
      const dateOfBirthControl = this.form.get('dateOfBirth');
      if (dateOfBirthControl?.value) {
        dateOfBirthControl.updateValueAndValidity();
      }
    });
  }

  public onOwnerCheckboxChange(event: any): void {
    const isOwner = event.checked;
    console.log('Owner checkbox changed:', isOwner);
    this.form.patchValue({ isOwner });
    this.ownerStateService.setIsOwner(isOwner);
      const currentFormValue = this.form.getRawValue();
  this.dataStorageService.setItem('personalDetailsForm', currentFormValue);
  }

  /**
   * Opens a dialog with the specified content type
   * @param contentType Type of content to display in the dialog
   */
  public openDialog(contentType: string): void {
    event?.preventDefault();
    
    this.dialog.open(DialogContentComponent, {
      width: '600px',
      maxWidth: 'calc(100vw - 32px)',
      data: { type: contentType }
    });
  }

  public onlyNumberKey(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  public toggleSSNVisibility(event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.ssnMasked = !this.ssnMasked;
  }

  public onSsnKeyDown(event: KeyboardEvent): void {
    clearMaskedSsnOnKeydown(this.ssnControl, event);
  }

  public onSsnPaste(event: ClipboardEvent): void {
    clearMaskedSsnOnPaste(this.ssnControl, event);
  }

  public onDobKeyDown(event: KeyboardEvent): void {
    clearMaskedDobOnKeydown(this.dobControl, event);
  }

  public onDobPaste(event: ClipboardEvent): void {
    clearMaskedDobOnPaste(this.dobControl, event);
  }

  private get ssnControl(): AbstractControl | null {
    return this.form.get('ssn');
  }

  private get dobControl(): AbstractControl | null {
    return this.form.get('dateOfBirth');
  }
}
